import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { toggleSpinner } from 'apps/auth/actions/appAction';
import { changeFormFieldValue, replaceFields, setFieldError, setNewFields } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import Form from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace, getEmptyNotValidFields, getSortFields } from 'apps/auth/components/form-multi/utils';
import BasePage from 'apps/auth/components/utils/BasePage';
import { NATIONALITY, RESIDENT_COUNTRY } from 'apps/auth/constants/fieldsName';
import { PROFILE_EDIT_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PROFILE_FIELDS, AUTH_PROFILE_FIELDS_UPDATE, AUTH_PROFILE_FIELDS_UPDATE_GET_NEW_STEP } from 'apps/auth/constants/UrlConstants';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { fetchCountryProfileFields } from 'apps/auth/services/ProfileService';

const formName = PROFILE_EDIT_FORM_NAME;

const ProfileFieldsEditNewApi = (props) => {
    const navigate = useNavigate();
    const submitRef = useRef(null);
    const fields = getSortFields(Object.values(useFields(formName)));
    const { numberOfSteps } = useExtra();
    const { continueButton, nextButton, completeButton, title, subTitle } = usePageMessages();
    const [step, setStep] = useState(0);
    const [disabledSubmitButton, setDisabledSubmitButton] = useState(true);

    useEffect(() => {
        const errorFields = getEmptyNotValidFields(fields);
        setDisabledSubmitButton(errorFields?.length > 0);
    }, [fields]);

    const isLastStep = () => step === numberOfSteps - 1;

    const openNextStepOrFinish = () => {
        const nextStep = step + 1;
        if (nextStep < numberOfSteps) {
            setStep(nextStep);
            return;
        }
        props.toggleSpinner(true);
        navigate(AUTH_PROFILE_FIELDS);
    };

    const clearFieldsOnCountrySelect = () => {
        fields.forEach((field) => {
            if (field.name !== RESIDENT_COUNTRY) {
                props.changeFormFieldValue(formName, null, field.name);
            }
        });
    };

    const chooseCountry = (country) => {
        const residentCountry = fields?.[RESIDENT_COUNTRY];
        if (!country?.code || residentCountry?.code === country.code) {
            return;
        }
        props.toggleSpinner(true);
        fetchCountryProfileFields(country.code)
            .then(({ fields: newFields }) => {
                props.toggleSpinner(false);
                if (!newFields) {
                    return;
                }
                //чекать на regex?
                props.replaceFields(formName, newFields);
            })
            .catch((error) => {
                props.toggleSpinner(false);
                logWarn(error);
            });
    };

    const submitHandler = (_responseFields, json) => {
        if (json?.messages) {
            props.updatePageMessages(json.messages);
        }
        if (json?.extra?.errors && Object.keys(json.extra.errors).length > 0) {
            Object.keys(json.extra.errors).forEach((name) => props.setFieldError(formName, name, json.extra.errors[name]));
            return;
        }
        if (json.forms[formName]?.fields) {
            props.replaceFields(formName, json.forms[formName].fields);
        }
        openNextStepOrFinish();
    };

    const beforeSubmit = () => clearLeadingSpace(fields, formName, props.changeFormFieldValue);

    const onContinueClick = () => {
        if (disabledSubmitButton) {
            return;
        }
        submitRef.current?.click();
    };
    const getTextForButton = () => {
        if (fields.filter((f) => !f.isReadOnly).length === 0) {
            return nextButton;
        }

        return isLastStep() ? completeButton : continueButton;
    };

    const getAction = () => {
        if (isLastStep()) {
            return AUTH_PROFILE_FIELDS_UPDATE;
        }
        return AUTH_PROFILE_FIELDS_UPDATE_GET_NEW_STEP + (step + 1);
    };

    if (!fields) {
        return false;
    }

    return (
        <CommonPage showChat={false} backUrl={AUTH_PROFILE_FIELDS} hasBottomPanel leftCloseIcon>
            <HeaderPanel title={title} subtitle={subTitle} />
            <Form
                id="updateProfileInfo"
                formName={formName}
                action={getAction()}
                getCanonicalPath={true}
                checkNotVisibleField={false}
                onSubmitHandler={submitHandler}
                fieldsForSubmit={fields}
                beforeSubmit={beforeSubmit}
            >
                <fieldset>
                    {fields.map((field) => {
                        return (
                            <fieldset key={`${formName}-fields-${field.name}`}>
                                <InputAutoDetect
                                    formName={formName}
                                    field={field}
                                    clearFieldsOnCountrySelect={clearFieldsOnCountrySelect}
                                    hasCountryItemPanel={field.name === RESIDENT_COUNTRY || field.name === NATIONALITY}
                                    selectHandler={chooseCountry}
                                />
                            </fieldset>
                        );
                    })}
                </fieldset>
                <FixedBottomPanel isRightAligned>
                    <Button
                        type="button"
                        form="updateProfileInfo"
                        raised={true}
                        caption={getTextForButton()}
                        disabled={disabledSubmitButton}
                        onClick={onContinueClick}
                    />
                </FixedBottomPanel>
                <input type="submit" ref={submitRef} className="hidden" />
            </Form>
        </CommonPage>
    );
};

export default connect(null, {
    toggleSpinner,
    updatePageMessages,
    setFieldError,
    setNewFields,
    replaceFields,
    changeFormFieldValue,
})(BasePage(ProfileFieldsEditNewApi));
